// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the workflow_runs VCA collection.
 *
 * CI/CD pipeline runs from any connected provider
 * (GitHub Actions, GitLab Pipelines, Forgejo Actions, etc.).
 *
 * Identity tuple: (gitConnectionId, repoId, runId)
 * ID prefix: wfr_
 */

import { z } from 'zod'

export const workflow_runsSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Git connection reference ──────────────────────────────────────────
  gitConnectionId: z.string().max(100),
  codebaseId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  repoId: z.string().max(200),
  /** Provider-assigned run/pipeline identifier (stable, numeric or string). */
  runId: z.string().max(200),

  // ── Run metadata ──────────────────────────────────────────────────────
  /** Workflow / pipeline name as defined in the repository configuration. */
  name: z.string().max(500),
  /** Current lifecycle status. */
  status: z.enum(['queued', 'in_progress', 'completed']),
  /**
   * Terminal outcome. Only set when status === 'completed'.
   * null while still running or queued.
   */
  conclusion: z.enum(['success', 'failure', 'cancelled', 'skipped']).nullable().optional(),

  // ── Commit / branch context ───────────────────────────────────────────
  /** SHA of the commit this run was triggered against. */
  headSha: z.string().max(100),
  /** Branch name that triggered the run (null for tag-triggered runs). */
  branchName: z.string().max(500).nullable().optional(),

  // ── Timing ───────────────────────────────────────────────────────────
  runStartedAt: z.string().datetime(),
  /** Set when status transitions to 'completed'. */
  completedAt: z.string().datetime().nullable().optional(),
  /** Wall-clock duration in milliseconds. Null while still running. */
  durationMs: z.number().int().nullable().optional(),

  // ── Provider-specific extras ──────────────────────────────────────────
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type Workflow_runsInput = z.infer<typeof workflow_runsSchema>
